<template>
  <detail-layout-full-width>
    <template slot="header">
      <base-header
        icon="user"
        :content="header"
      />
    </template>
    <template slot="main">
      <div class="form-wrapper">
        <el-form
          :model="form"
          :rules="rules"
          ref="ruleForm"
        >
        <h2 class="dark:text-darkblue-200 font-bold mt-4 mb-4">{{$t('companies.group_company_data')}}</h2>
        <div class="lg:max-w-detail lg:mr-auto" v-if="(role === 'AdminDistribuidor' || role === 'SuperAdminDistribuidor' || role === 'SuperAdministrador')">
        <div class="lg:mb-2" >
            <div class="flex justify-between items-center">
              <div class="flex-1">
                <AccessibleElSelect
                  v-model="form.clienteId"
                  prop="clienteId"
                  :options="clientesForAccessibleSelect"
                  :withFormItem="true"
                  :isDisabled="!isCreate"
                  id-prefix="company"
                >
                  <template slot="label"><base-obligatory-icon v-if="isCreate"/>{{$t('app.client')}}</template>
                </AccessibleElSelect>
              </div>
              <base-button 
                v-if="!isCreate" 
                class="ml-4" 
                mode="primary" 
                :text="$t('app.detail')" 
                @click.prevent="goDetailClient" 
                :loading="isGeneralLoading"/>
            </div>
          </div>
            </div>
        <div class="lg:max-w-detail lg:mr-auto">
          <div class="lg:mb-2">
            <div class="mandatory">
              <label class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon/>{{$t('companies.companyName')}}
                <el-form-item prop="NombreCIF">
                  <el-input
                    :disabled="isRoleUserOrConsulta"
                    class="w-full"
                    v-model="form.NombreCIF"
                    maxlength="150"
                    >
                    <template v-if="form.NombreCIF && !isRoleUserOrConsulta" slot="suffix">
                      <base-clear-input-button
                        aria-label="Limpiar campo de nombre de empresa"
                        @clear="form.NombreCIF = ''"
                      />
                    </template>
                  </el-input>
                </el-form-item>
              </label>
            </div>
          </div>
          <div class="lg:mb-2">
            <div class="mandatory">
              <label class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon v-if="isCreate"/>{{$t('companies.cif')}}
                <el-form-item prop="CIF">
                  <el-input
                    :disabled="!isCreate || isRoleUserOrConsulta"
                    class="w-full"
                    v-model="form.CIF"
                    maxlength='50'
                    >
                    <template v-if="form.CIF && !(!isCreate || isRoleUserOrConsulta)" slot="suffix">
                      <base-clear-input-button
                        aria-label="Limpiar campo de CIF"
                        @clear="form.CIF = ''"
                      />
                    </template>
                  </el-input>
                </el-form-item>
              </label>
            </div>
          </div>
          <div class="lg:mb-2">
              <div class="mandatory">
                <label class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon v-if="isSageWeb"/>{{$t('clients.cod_ext')}}
                  <el-form-item prop="CodigoExt">
                    <el-input
                      class="w-full"
                      :disabled="isRoleUserOrConsulta"
                      v-model="form.CodigoExt"
                      maxlength='20'
                      >
                      <template v-if="form.CodigoExt && !isRoleUserOrConsulta" slot="suffix">
                        <base-clear-input-button
                          aria-label="Limpiar campo de código externo"
                          @clear="form.CodigoExt = ''"
                        />
                      </template>
                    </el-input>
                  </el-form-item>
                </label>
              </div>
            </div>
          <div class="lg:mb-4 flex items-center">
            <div class="block">
              <div class="flex items-center mb-1">
                <span id="company-fecha-inicio-vig-label" class="text-sm font-semibold dark:text-darkblue-200 text-secondary-600">
                  {{$t('companies.date_vigilante')}}
                </span>
                <base-tooltip-icon 
                  size="sm" 
                  :section="$t('companies.date_vigilante')"
                  :content="$t('tooltip.fechaInicioVig')" 
                  class="ml-2"/>
              </div>
              <el-form-item prop="fechaInicioVig">
                <el-date-picker
                  :clearable="false"
                  :disabled="isRoleUserOrConsulta"
                  class="bg-white dark:bg-darkblue-900"
                  v-model="form.fechaInicioVig"
                  :picker-options="dateRangeOptions"
                  type="date"
                  format="dd-MM-yyyy"
                  placeholder="dd-mm-aaaa"
                  aria-labelledby="company-fecha-inicio-vig-label"
                  ref="datePickerFechaInicioVig"
                  @blur="onBlurDatePickerMixin('.datePickerFechaInicioVig-popover')"
                  @focus="onFocusDatePickerMixin('.datePickerFechaInicioVig-popover')"
                  @keydown.native="captureKeyNotChrome"
                  popper-class="datePickerFechaInicioVig-popover"
                  >
                </el-date-picker>
              </el-form-item>
            </div>
            <base-clear-input-button
              v-if="form.fechaInicioVig"
              aria-label="Limpiar fecha de inicio de vigilancia"
              @clear="form.fechaInicioVig = null"
            />
          </div>
          <div class="mb-6">
            <label for="switch-company-status" class="text-sm font-semibold text-secondary-600 block mb-1 flex flex-col">
              {{$t('companies.status')}}
              <span class="sr-only">{{$t('app.options')}}: {{$t('app.inactive')}} / {{$t('configurations.active')}}</span>
            </label>
            <el-switch 
              id="switch-company-status"
              :aria-label="$t('companies.status')"
              class="switch-text break-normal mt-2" 
              :disabled="isRoleUserOrConsulta" 
              v-model="form.Activo" 
              :active-text="$t('configurations.active')" 
              />
          </div>
           <div class="lg:mb-2">
              <div class="mandatory" v-if="this.$route.params.id != -1">
                <base-ro-input-minimal :label="$t('companies.created_at')" :data="form.FechaAlta | date"/>
              </div>
          </div>
          
        <!-- <h2 class="dark:text-darkblue-200 font-bold mt-6 mb-4">{{$t('companies.group_alert')}}</h2> -->

          <base-collapsible-form 
            ref="baseCollapsibleAlerts" 
            class="mt-2 bg-white dark:bg-darkblue-900 rounded-lg border-solid border-primary-300 dark:border-darkblue-600 border pr-4 pl-4 mb-2">
            <template slot="headerRight">
              <h2 class="dark:text-darkblue-200 font-bold mt-4 mb-4 text-sm">{{$t('companies.group_alert')}}</h2>
            </template>
            <template slot="content">

              <div class="mt-2 mb-4">
                <label for="switch-group-alert-active" class="text-sm font-semibold text-secondary-600 block mb-1 flex flex-col">
                  {{$t('companies.group_alert_active')}}
                  <span class="sr-only">{{$t('app.inactive')}} / {{$t('companies.active')}}</span>
                </label>
                <el-switch 
                  id="switch-group-alert-active"
                  :aria-label="$t('companies.group_alert_active')"
                  class="switch-text break-normal mt-2" 
                  :disabled="isRoleUserOrConsulta" 
                  v-model="form.EnviarAvisos"
                  :active-text="$t('companies.active')">
                </el-switch>
              </div>

              <div class="lg:mb-2">
                <div class="mandatory">
                  <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('companies.email_alerts')}}
                    <el-form-item prop="EmailAvisoNueva">
                      <el-input
                        :disabled="isRoleUserOrConsulta"
                        class="w-full"
                        v-model="form.EmailAvisoNueva"
                        :maxlength='maxLengthCorreos'
                        >
                        <template v-if="form.EmailAvisoNueva && !isRoleUserOrConsulta" slot="suffix">
                          <base-clear-input-button
                            aria-label="Limpiar campo de emails"
                            @clear="form.EmailAvisoNueva = ''"
                          />
                        </template>
                      </el-input>
                    </el-form-item>
                  </label>
                </div>
              </div>
              <div class="lg:mb-2">
                <div class="mandatory">
                  <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('companies.email_downloads')}}
                    <el-form-item prop="EmailAvisoDescargada">
                      <el-input
                        :disabled="isRoleUserOrConsulta"
                        class="w-full"
                        v-model="form.EmailAvisoDescargada"
                        :maxlength='maxLengthCorreos'
                        >
                        <template v-if="form.EmailAvisoDescargada && !isRoleUserOrConsulta" slot="suffix">
                          <base-clear-input-button
                            aria-label="Limpiar campo de emails"
                            @clear="form.EmailAvisoDescargada = ''"
                          />
                        </template>
                      </el-input>
                    </el-form-item>
                  </label>
                </div>
              </div>
              
              <div class="lg:mb-2" v-if="hasPermModuleSMS">
                <div class="mandatory">
                  <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('companies.sms_downloads')}}
                    <el-form-item prop="SMSAvisoDescargada">
                      <el-input
                        :disabled="isRoleUserOrConsulta"
                        class="w-full"
                        maxlength="9"
                        v-model="form.SMSAvisoDescargada"
                      >
                        <template slot="prepend">{{formatCodigoPaisSMSAvisoDescargada}}</template>
                        <template v-if="form.SMSAvisoDescargada && !isRoleUserOrConsulta" slot="suffix">
                          <base-clear-input-button
                            class="pt-2"
                            aria-label="Limpiar campo de SMS"
                            @clear="form.SMSAvisoDescargada = ''"
                          />
                        </template>
                      </el-input>
                    </el-form-item>
                  </label>
                </div>
              </div>

              <div class="lg:mb-2">
                <div class="mandatory">
                  <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('companies.email_tramitada')}}
                    <el-form-item prop="EmailAvisoTramitada">
                      <el-input
                        :disabled="isRoleUserOrConsulta"
                        class="w-full"
                        v-model="form.EmailAvisoTramitada"
                        :maxlength='maxLengthCorreos'
                        >
                        <template v-if="form.EmailAvisoTramitada && !isRoleUserOrConsulta" slot="suffix">
                          <base-clear-input-button
                            class="pt-2"
                            aria-label="Limpiar campo de emaisl"
                            @clear="form.EmailAvisoTramitada = ''"
                          />
                        </template>
                      </el-input>
                    </el-form-item>
                  </label>
                </div>
              </div>

              <div class="lg:mb-0">
                <div class="flex -mx-2 flex-col lg:flex-row">
                  <div class="px-2 w-full lg:mb-0">
                    <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('companies.download_alert_days_form')}}
                      <el-form-item prop="DiasAviso" class="mb-0">
                        <el-input
                          :disabled="isRoleUserOrConsulta"
                          class="w-full"
                          v-model="form.DiasAviso"
                          maxlength='5'
                          :placeholder="$t('configurations.default_disabled')"
                          >
                          <template v-if="form.DiasAviso && !isRoleUserOrConsulta" slot="suffix">
                            <base-clear-input-button
                              aria-label="Limpiar campo de días de aviso"
                              @clear="form.DiasAviso = ''"
                            />
                          </template>
                        </el-input>
                      </el-form-item>
                    </label>
                  </div>
                  <div class="px-2 w-full lg:mb-0">
                    <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('companies.end_alert_days_label_form')}}
                      <el-form-item prop="DiasAvisoFinTramite" class="mb-0">
                        <el-input
                          :disabled="isRoleUserOrConsulta"
                          class="w-full"
                          v-model="form.DiasAvisoFinTramite"
                          maxlength='5'
                          :placeholder="$t('configurations.default_disabled')"
                          >
                          <template v-if="form.DiasAvisoFinTramite && !isRoleUserOrConsulta" slot="suffix">
                            <base-clear-input-button
                              aria-label="Limpiar campo de días de aviso fin trámite"
                              @clear="form.DiasAvisoFinTramite = ''"
                            />
                          </template>
                        </el-input>
                      </el-form-item>
                    </label>
                  </div>
                </div>
              </div>

              <div class="lg:mb-2">
                <div class="mt-5 mb-5 mr-20">
                  <label for="switch-mails-group-status" class="text-sm font-semibold text-secondary-600 block mb-1 flex flex-col">
                    {{$t('configurations.mails_group_status')}}
                    <span class="sr-only">{{$t('app.inactive')}} / {{$t('companies.active')}}</span>
                  </label>
                  <el-switch 
                    id="switch-mails-group-status"
                    :aria-label="$t('configurations.mails_group_status')"
                    class="switch-text break-normal mt-2" 
                    :disabled="isRoleUserOrConsulta" 
                    v-model="form.AgruparEmailsAvisos"
                    :active-text="$t('companies.active')">
                  </el-switch>
                </div>
              </div>

              <div class="lg:mb-2">
                <div class="mandatory">
                  <label class="text-sm font-semibold text-secondary-600 block mb-1">{{form.AgruparEmailsAvisos ? $t('companies.mails_group_status_input'):$t('companies.email_ultima_llamada')}}
                    <el-form-item prop="EmailAvisoUltimaLlamada">
                      <el-input
                        :disabled="isRoleUserOrConsulta"
                        class="w-full"
                        v-model="form.EmailAvisoUltimaLlamada"
                        :maxlength='maxLengthCorreos'
                        >
                        <template v-if="form.EmailAvisoUltimaLlamada && !isRoleUserOrConsulta" slot="suffix">
                          <base-clear-input-button
                            aria-label="Limpiar campo de emails"
                            @clear="form.EmailAvisoUltimaLlamada = ''"
                          />
                        </template>
                      </el-input>
                    </el-form-item>
                  </label>
                </div>
              </div>
              <div class="lg:mb-2" v-if="form.AgruparEmailsAvisos === false">
                <div class="mandatory">
                  <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('companies.email_tramitacion_urgente')}}
                    <el-form-item prop="EmailAvisoTramitacionUrgente">
                      <el-input
                        :disabled="isRoleUserOrConsulta"
                        class="w-full"
                        v-model="form.EmailAvisoTramitacionUrgente"
                        :maxlength='maxLengthCorreos'
                        >
                        <template v-if="form.EmailAvisoTramitacionUrgente && !isRoleUserOrConsulta" slot="suffix">
                          <base-clear-input-button
                            aria-label="Limpiar campo de emails"
                            @clear="form.EmailAvisoTramitacionUrgente = ''"
                          />
                        </template>
                      </el-input>
                    </el-form-item>
                  </label>
                </div>
              </div>
              <div class="lg:mb-2" v-if="form.AgruparEmailsAvisos === false">
                <div class="mandatory">
                  <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('companies.email_neos_error')}}
                    <el-form-item prop="EmailAvisoErrores">
                      <el-input
                        :disabled="isRoleUserOrConsulta"
                        class="w-full"
                        v-model="form.EmailAvisoErrores"
                        :maxlength='maxLengthCorreos'
                        >
                        <template v-if="form.EmailAvisoErrores && !isRoleUserOrConsulta" slot="suffix">
                          <base-clear-input-button
                            aria-label="Limpiar campo de emails"
                            @clear="form.EmailAvisoErrores = ''"
                          />
                        </template>
                      </el-input>
                    </el-form-item>
                  </label>
                </div>
              </div>

              <div class="lg:mb-2">
                <div class="mandatory">
                  <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('companies.email_tech')}}
                    <el-form-item prop="EmailTecnico" ref="EmailTecnico">
                      <!-- <base-email-list 
                        v-show="!modeEmail"
                        nameType="email" 
                        :emitEvent="'sendDataCorreosTecnicos'" 
                        @sendDataCorreosTecnicos="sendDataCorreosTecnicos"
                        :tags="form.EmailTecnico"
                        class="mb-2"
                      /> -->
                      <el-input
                        :disabled="isRoleUserOrConsulta"
                        class="w-full"
                        v-model="form.EmailTecnico"
                        :maxlength='maxLengthCorreos'
                        >
                        <template v-if="form.EmailTecnico && !isRoleUserOrConsulta" slot="suffix">
                          <base-clear-input-button
                            aria-label="Limpiar campo de emails"
                            @clear="form.EmailTecnico = ''"
                          />
                        </template>
                      </el-input>
                    </el-form-item>
                  </label>
                </div>
              </div>

            </template>
          </base-collapsible-form>

          <!-- <h2 class="dark:text-darkblue-200 font-bold mt-8 mb-4">{{$t('companies.automatic_download')}}</h2> -->

          <base-collapsible-form 
            ref="baseCollapsibleAutoDownload" 
            class="mt-2 bg-white dark:bg-darkblue-900 rounded-lg border-solid border-primary-300 dark:border-darkblue-600 border pr-4 pl-4 mb-2">
            <template slot="headerRight">
              <h2 class="dark:text-darkblue-200 font-bold mt-4 mb-4 text-sm">{{$t('companies.automatic_download')}}</h2>
              <base-tooltip-icon size="sm" :section="$t('companies.automatic_download')" :content="$t('tooltip.companies.auto_download')" class="ml-2"/>
            </template>
            <template slot="content">

              <div class="lg:mb-2">
                <div class="mt-2 mb-5 mr-20">
                  <label for="switch-automatic-download" class="text-sm font-semibold text-secondary-600 block mb-1 flex flex-col">
                    {{$t('companies.automatic_download_active')}}
                    <span class="sr-only">{{$t('app.inactive')}} / {{$t('companies.active')}}</span>
                  </label>
                  <el-switch 
                    id="switch-automatic-download"
                    :aria-label="$t('companies.automatic_download_active')"
                    class="switch-text break-normal mt-2" 
                    :disabled="isRoleUserOrConsulta" 
                    v-model="form.DescargaAutomatica"
                    :active-text="$t('companies.active')">
                  </el-switch>
                </div>
              </div>
              <div class="lg:mb-2">
                  <div class="mandatory">
                    
                    <label class="text-sm font-semibold text-secondary-600 block mb-1">{{$t('companies.automatic_download_days_label')}}
                      <el-form-item prop="DiasDescargaAutomatica">
                        <el-input
                          :disabled="isRoleUserOrConsulta"
                          class="w-full"
                          v-model="form.DiasDescargaAutomatica"
                          maxlength='5'
                          >
                          <template v-if="form.DiasDescargaAutomatica && !isRoleUserOrConsulta" slot="suffix">
                            <base-clear-input-button
                              aria-label="Limpiar campo de días de descarga automática"
                              @clear="form.DiasDescargaAutomatica = ''"
                            />
                          </template>
                        </el-input>
                      </el-form-item>
                    </label>
                  </div>
              </div>

            </template>
          </base-collapsible-form>

          <div v-if="!isCreate" class="rounded-md bg-secondary-50 p-1 dark:text-darkblue-400 dark:bg-darkblue-900 mt-8" >
            <div>
              <div v-if="form.CN" class="flex space-between items-end m-2">
                <div class="w-full">
                  <h2 class="px-2 w-full mb-2 lg:mb-0 font-bold dark:text-secondary-200">Certificado</h2>
                  <div  class="px-2 w-full mt-2">
                    <base-ro-input :label="$t('certificates.name')" :data="form.CN"/>
                  </div>
                </div>
                <base-button v-if="form.verDetalle" class="mb-1" mode="primary" :text="$t('app.detail')" @click.prevent.stop="goDetailCert" :loading="isGeneralLoading"/>
              </div>
              <div v-else class="flex space-between items-center m-2">
                <h2 class="w-full font-bold">Sin certificado asociado</h2>
                <base-button v-if="!isRoleUserOrConsulta" class="mb-1" mode="primary" :text="$t('companies.add_certificate')" @click.prevent.stop="togglelinkCertificateDialog" :loading="isGeneralLoading"/>
              </div>
            </div>
          </div>
          <div v-if="!isCreate" class="mt-4 mb-4">
            <div v-if="!form.CN">
              <label for="switch-company-alert_delegated" class="text-sm font-semibold text-secondary-600 block mb-1 flex flex-col">
                {{$t('companies.dont_alert_delegated')}}
                <span class="sr-only">{{$t('app.options')}}: {{$t('app.inactive')}} / {{$t('configurations.active')}}</span>
              </label>
              <el-switch 
                id="switch-company-alert_delegated"
                :aria-label="$t('companies.dont_alert_delegated')"
                class="switch-text break-normal mt-2" 
                :disabled="isRoleUserOrConsulta" 
                v-model="form.ConPoderesDelegados" 
                :active-text="$t('configurations.active')" 
                />
            </div>
          </div>

        </div>
        </el-form>
      </div>
      </template>
    <template slot="layout-extend">
      <base-footer>
        <template slot="actions">
          <base-button-responsive mode="secondary" :text="isCreate ? $t('app.cancel') : $t('app.back')" icon="cross" @click="goBack" :loading="isGeneralLoading"/>
          <!-- <base-button-responsive mode="primary-phantom" :text="$t('app.change_mode_email')" icon="mail-config" @click="changeModeEmail" :loading="isGeneralLoading"/> -->
          <!-- <base-button-responsive v-if="!isCreate" mode="primary" :text="!hasCertificate ? $t('companies.add_certificate') : $t('companies.replace_certificate')" icon="certificates" @click="addReplaceCertificate" :loading="isGeneralLoading"/> -->
          <base-button-responsive v-if="!isRoleUserOrConsulta" mode="primary" :text="$t('app.save')" icon="save" @click="submitForm('ruleForm')" :loading="isGeneralLoading" :disabled="isGeneralLoading"/>
        </template>
      </base-footer>

      <link-certificate-dialog 
        v-if="!role || !isRoleUserOrConsulta"
        :dialogOpen="linkCertificateDialog" 
        @toggleDialog="togglelinkCertificateDialog" 
        @applyDialog="applylinkCertificateDialog" 
        :clientId="this.form.clienteId"
        />
    </template>
  </detail-layout-full-width>
</template>

<script>
import DetailLayoutFullWidth from '@/components/layout/DetailLayoutFullWidth.vue'
import AccessibleElSelect from '@/components/elementui/AccessibleElSelect.vue'
import { getCompany } from '@/services/companies/getCompany'
// import { getCertificateFromCif } from '@/services/companies/getCertificateFromCif'
import { updateCompany } from '@/services/companies/updateCompany'
import { createCompany } from '@/services/companies/createCompany'
import { linkCompany } from '@/services/companies/linkCompany'
import { getCompanyTemplate } from '@/services/app/getCompanyTemplate'
import { getClients } from '@/services/clients/getClients'
import moment from 'moment'
import { email as emailRegex } from '@/utils/regex'
import { i18n } from '@/i18n'
import LinkCertificateDialog from '@/components/LinkCertificateDialog.vue'
import { linkCertificate } from '@/services/certificates/linkCertificate'

function isValidEmail (value) {
  return value.split(';').every(item => emailRegex.test(item))
}
function isValidDayDownload (value) {
  if (Number.isInteger(parseFloat(value))) {
    if (value >= 0 && value <= 10) {
      return true
    }
  }
  return false
}
function isValidPhoneNumberES (value) {
  const pattern = /^\d{9}$/
  if (Number.isInteger(parseFloat(value))) {
    if (value.match(pattern)) {
      return true
    }
  }
  return false
}
function isValidDay (value) {
  if (Number.isInteger(parseFloat(value))) {
    if (value >= 0 && value <= 8) {
      return true
    }
  }
  return false
}
function isValidDayEnd (value) {
  if (Number.isInteger(parseFloat(value))) {
    if (value >= 0 && value <= 30) {
      return true
    }
  }
  return false
}

export default {
  /* eslint-disable camelcase */
  name: 'template-detail',
  components: {
    DetailLayoutFullWidth,
    LinkCertificateDialog,
    AccessibleElSelect,
  },
  data: function () {
    return {
      dateRangeOptions: {
        firstDayOfWeek: 1,
      },
      searchView: false,
      activeName: 'es',
      vuexnamespace: 'companies',
      clientes: [],
      header: {
        title: 'DETALLE DE EMPRESA',
        icon: 'companies',
        breadcrumb: {},
        nav: [],
        url: 'companies'
      },
      maxLengthCorreos: 500,
      modeEmail: false,
      linkCertificateDialog: false,
      form: {
        IdCIFEmpresa: 0,
        IdEmpresa: 0,
        CIF: '',
        CN: null,
        verDetalle: false,
        SerialNumber: null,
        TipoCertificado: null,
        NombreCIF: '',
        EmailAvisoNueva: '',
        EmailAvisoDescargada: '',
        CodigoPaisSMSAvisoDescargada: '0034',
        SMSAvisoDescargada: '',
        EmailAvisoTramitada: '',
        DescargaAutomatica: false,
        DiasDescargaAutomatica: 0,
        Activo: true,
        ConPoderesDelegados: false,
        DiasAviso: null,
        DiasAvisoFinTramite: null,
        AgruparEmailsAvisos: false,
        EmailAvisoUltimaLlamada: '',
        EmailAvisoTramitacionUrgente: '',
        EmailAvisoErrores: '',
        EmailTecnico: '',
        FechaAlta: '',
        FechaBaja: '',
        CodigoExt: '',
        clienteId: null,
        EnviarAvisos: true,
        fechaInicioVig: '',
      },
      certificate: null,
    }
  },
  computed: {
    empresaFilter () {
      return this.isCreate ? this.$store.getters['companies/getMasterFilters']?.empresa?.value || null : null
    },
    formatCodigoPaisSMSAvisoDescargada () {
      if (this.form?.CodigoPaisSMSAvisoDescargada === '0034') return '+34'
      return 'Desconocido'
    },
    hasPermModuleSMS () {
      return this.$store.getters['auth/hasModuleEnvioSMS']
    },
    id: function () {
      return this.$route.params.id
    },
    role: function () {
      return this.$store.getters['auth/role']
    },
    isRoleUserOrConsulta: function () {
      return this.role === 'Usuario' || this.role === 'UsuarioConsulta'
    },
    isCreate: function () {
      return this.id === -1 || this.id === '-1'
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    hasCertificate: function () {
      return this.form.CN !== null && this.form.CN !== ''
    },
    clientesForAccessibleSelect () {
      return this.clientes.map(cliente => ({
        id: cliente.IdEmpresa,
        name: cliente.Nombre,
        value: cliente.IdEmpresa
      }))
    },
    idTipoServicioCliente: function () {
      return this.$store.getters['auth/idTipoServicioCliente']
    },
    isSageWeb () {
      if (this.role === 'AdminDistribuidor' || this.role === 'SuperAdminDistribuidor' || this.role === 'SuperAdministrador') {
        if (this.form?.clienteId) { // Si tiene cliente seleccionado y es tipo SageWeb
          const clienteSelect = this.clientes.filter(x => x.IdEmpresa === this.form?.clienteId)
          if (clienteSelect.length > 0 && clienteSelect[0].idTipoServicio === 7) {
            return true
          }
        }
      } else if (this.idTipoServicioCliente === 7 || this.idTipoServicioCliente === '7') { // Administrador con cliente SageWeb
        return true
      }
      return false
    },
    rules () {
      return {
        clienteId: [
          { validator: this.isGenericValidation, trigger: 'blur' },
          { validator: this.isGenericValidation, trigger: 'change' }
        ],
        NombreCIF: [
          { validator: this.isGenericValidation, trigger: 'blur' }
        ],
        CodigoExt: [
          { validator: this.isSageWebValidation, trigger: 'blur' }
        ],
        CIF: [
          { validator: this.isGenericValidation, trigger: 'blur' }
        ],
        EmailAvisoNueva: [
          { required: false, validator: this.isEmailValid, trigger: 'blur' }
        ],
        EmailAvisoDescargada: [
          { required: false, validator: this.isEmailValid, trigger: 'blur' }
        ],
        SMSAvisoDescargada: [
          { required: false, validator: this.isNumberPhoneValid, trigger: 'blur' }
        ],
        EmailAvisoTramitada: [
          { required: false, validator: this.isEmailValid, trigger: 'blur' }
        ],
        EmailAvisoUltimaLlamada: [
          { required: false, validator: this.isEmailValid, trigger: 'blur' }
        ],
        EmailAvisoTramitacionUrgente: [
          { required: false, validator: this.isEmailValid, trigger: 'blur' }
        ],
        EmailAvisoErrores: [
          { required: false, validator: this.isEmailValid, trigger: 'blur' }
        ],
        EmailTecnico: [
          { required: false, validator: this.isEmailValid, trigger: 'blur' }
        ],
        DiasDescargaAutomatica: [
          { required: false, validator: this.isDayValidDownload, trigger: 'blur' }
        ],
        DiasAviso: [
          { required: false, validator: this.isDayValid, trigger: 'blur' }
        ],
        DiasAvisoFinTramite: [
          { required: false, validator: this.isDayValidEnd, trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    form: {
      deep: true,
      immediate: true,
      handler: 'watchFormHandler'
    }
  },
  created: async function () {
    const companyId = this.$route.params.id
    const breadcrumbs = []
    breadcrumbs.push({ path: 'companies', name: this.$i18n.t('app.companies') })
    if (this.role === 'AdminDistribuidor' || this.role === 'SuperAdminDistribuidor' || this.role === 'SuperAdministrador') {
      const dataClients = await getClients()
      if (dataClients.blnError === true) {
        this.doFeedback(dataClients.msgError + ' [Código de error: ' + dataClients.codError + ']')
      }
      this.clientes = dataClients.Empresas
      if (this.isCreate) this.preSelectClient(this.form, 'clienteId')
    }

    if (companyId !== -1 && companyId !== '-1') {
      await this.downloadCompanyData(companyId)
      breadcrumbs.push({ path: '', name: this.form.NombreCIF })
      this.$store.commit('app/setheaderBreadCrumb', this.form.NombreCIF)
      /*
      const certificate = await getCertificateFromCif({ cif: this.form.CIF })
      if (certificate.blnError === false) {
        this.certificate = certificate.Certificado
        this.certificate.CertFechaCaducidad = moment(certificate.Certificado.CertFechaCaducidad).format('YYYY-MM-DD')
      }
      */
    } else {
      breadcrumbs.push({ path: '', name: this.$i18n.t('companies.new_company') })
      if (this.role !== 'SuperAdministrador' && this.role !== 'SuperAdminDistribuidor' && this.role !== 'AdminDistribuidor') {
        const template = await getCompanyTemplate()
        if (template.blnError === true) {
          this.doFeedback(template.msgError + ' [Código de error: ' + template.codError + ']')
        }
        this.setCompany(template.respuesta)
      }
    }
    this.header.breadcrumb = breadcrumbs
  },
  mounted () {
    this.resetControlFormChanges()
  },
  methods: {
    isSageWebValidation: function (rule, value, callback) {
      if (!this.isSageWeb) callback()
      if (!value) {
        return this.doAriaLiveAndReturnCallback(i18n.t('validation.complete'), callback)
      } else {
        callback()
      }
    },
    togglelinkCertificateDialog () {
      this.linkCertificateDialog = !this.linkCertificateDialog
    },
    applylinkCertificateDialog: async function (certificate) {
      this.$store.commit('setAjaxRequestPending', true)
      this.$store.commit('setIsLoading', true)
      this.togglelinkCertificateDialog()
      try {
        const idCIFEmpresa = parseInt(this.$route.params.id)
        const params = {
          idCIFS: [idCIFEmpresa],
          serialCert: certificate.Serialnumber,
          tipoCertificado: certificate.TipoCertificado,
          idEmpresa: certificate.idEmpresa
        }
        const response = await linkCertificate(params)
        if (response.blnError === true) {
          this.doFeedback(response.msgError + ' [Código de error: ' + response.codError + ']')
        } else {
          this.doFeedback('Vinculado correctamente', 'success')
          await this.downloadCompanyData(idCIFEmpresa)
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    addReplaceCertificate () {
      if (this.hasCertificate) {
      } else {
      }
    },
    changeModeEmail () {
      this.modeEmail = !this.modeEmail
      if (!this.modeEmail) {
        // Mail técnico
        const tmpArrayMails = this.form.EmailTecnico.split(';').filter(item => {
          if (emailRegex.test(item)) return item
        })
        this.form.EmailTecnico = tmpArrayMails.join(';')
        this.$refs.EmailTecnico.clearValidate()
      }
    },
    sendDataCorreosTecnicos (dataCorreos) {
      this.form.EmailTecnico = dataCorreos.join(';')
    },
    watchFormHandler: function ({ certificate, send_alerts, automatic_download, fechaInicioVig }) {
      if (fechaInicioVig !== undefined && moment(fechaInicioVig).isValid()) {
        this.form.fechaInicioVig = moment(fechaInicioVig).format('YYYY-MM-DD')
      } else {
        this.form.fechaInicioVig = ''
      }
      if (certificate !== undefined) {
        this.form.certificate = !!certificate
      }
      if (automatic_download !== undefined) {
        this.form.automatic_download = !!automatic_download
      }
      if (send_alerts !== undefined) {
        this.form.send_alerts = !!send_alerts
      }
      this.$store.commit('app/setFormChanges', true)
    },
    toggleSearchView: function () {
      this.searchView = !this.searchView
    },
    isEmailValid: function (rule, value, callback) {
      if (!value) {
        callback() // queremos que la validación sea opcional en este caso.
      } else if (!isValidEmail(value)) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.email_multiple'), callback)
      } else if (value.length > this.maxLengthCorreos) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.email_length') + this.maxLengthCorreos + ' caracteres', callback)
      } else {
        callback()
      }
    },
    isDayValid: function (rule, value, callback) {
      if (!value) {
        callback() // queremos que la validación sea opcional en este caso.
      } else if (!isValidDay(value)) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.template.aviso'), callback)
      } else {
        callback()
      }
    },
    isDayValidDownload: function (rule, value, callback) {
      if (!value) {
        callback() // queremos que la validación sea opcional en este caso.
      } else if (!isValidDayDownload(value)) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.template.aviso_download'), callback)
      } else {
        callback()
      }
    },
    isDayValidEnd: function (rule, value, callback) {
      if (!value) {
        callback() // queremos que la validación sea opcional en este caso.
      } else if (!isValidDayEnd(value)) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.template.aviso_fin_tramite'), callback)
      } else {
        callback()
      }
    },
    isNumberPhoneValid: function (rule, value, callback) {
      if (!value) {
        callback() // queremos que la validación sea opcional en este caso.
      } else if (!isValidPhoneNumberES(value)) {
        this.doAriaLiveAndReturnCallback(i18n.t('validation.template.sms'), callback)
      } else {
        callback()
      }
    },
    submitForm: async function (formName) {
      setTimeout(() => {
        this.$store.commit('setIsLoading', true)
        this.$refs[formName].validate(async valid => {
          if (valid) {
            const params = {
              CIF: this.form.CIF,
              NombreCIF: this.form.NombreCIF,
              EmailAvisoNueva: this.form.EmailAvisoNueva,
              EmailAvisoDescargada: this.form.EmailAvisoDescargada,
              NumeroSMSDescargadas: this.form.SMSAvisoDescargada ? (this.form.CodigoPaisSMSAvisoDescargada + '' + this.form.SMSAvisoDescargada) : null,
              EmailAvisoTramitada: this.form.EmailAvisoTramitada,
              EmailDiasAviso: this.form.EmailAvisoUltimaLlamada,
              EmailAvisoTramitacionUrgente: this.form.EmailAvisoTramitacionUrgente,
              EmailAvisoErrores: this.form.EmailAvisoErrores,
              AgruparEmailsAvisos: this.form.AgruparEmailsAvisos,
              EmailTecnico: this.form.EmailTecnico,
              DescargaAutomatica: this.form.DescargaAutomatica,
              EnviarAvisos: this.form.EnviarAvisos,
              DiasDescargaAutomatica: parseInt(this.form.DiasDescargaAutomatica),
              DiasAviso: parseInt(this.form.DiasAviso),
              DiasFinTramite: parseInt(this.form.DiasAvisoFinTramite),
              FechaAlta: this.form.FechaAlta ? this.form.FechaAlta : null,
              Activo: this.form.Activo,
              ConPoderesDelegados: this.form.ConPoderesDelegados,
              fechaInicioVig: this.form.fechaInicioVig ? moment(this.form.fechaInicioVig).format('YYYY-MM-DD[T00:00:00]') : null,
              CodigoExt: this.form.CodigoExt,
            }
            let response = null
            if (this.$route.params.id === -1 || this.$route.params.id === '-1') {
              params.idEmpresa = this.form.clienteId
              response = await createCompany(params)
              if (response && response.blnError === false) {
                if (this.role !== 'SuperAdministrador' && this.role !== 'SuperAdminDistribuidor' && this.role !== 'AdminDistribuidor') {
                  await this.$store.dispatch('auth/me', false)
                  const user = this.$store.getters['auth/user']
                  response = await linkCompany([{ idCIFEmpresa: response.IdCIFEmpresa, idUsuario: user.IdUsuario }])
                } else {
                  response = { blnError: false }
                }
                if (response && response.blnError === false) {
                  if (this.role !== 'SuperAdministrador' && this.role !== 'SuperAdminDistribuidor' && this.role !== 'AdminDistribuidor') {
                    const template = await getCompanyTemplate()
                    if (template.blnError === true) {
                      this.doFeedback(template.msgError + ' [Código de error: ' + template.codError + ']')
                    } else {
                      this.doFeedback('Datos guardados correctamente', 'success')
                      this.resetForm()
                      this.setCompany(template.respuesta)
                      this.resetControlFormChanges()
                    }
                  } else {
                    this.doFeedback('Datos guardados correctamente', 'success')
                    this.resetCompany()
                    setTimeout(() => {
                      this.resetForm()
                      this.resetControlFormChanges()
                      if (this.role === 'AdminDistribuidor' || this.role === 'SuperAdminDistribuidor' || this.role === 'SuperAdministrador') {
                        this.preSelectClient(this.form, 'clienteId')
                      }
                    }, 200)
                  }
                } else {
                  this.doFeedback(response.msgError + ' [Código de error: ' + response.codError + ']')
                }
              } else {
                this.doFeedback(response.msgError + ' [Código de error: ' + response.codError + ']')
              }
            } else {
              response = await updateCompany(params, this.$route.params.id)
              if (response && response.blnError === false) {
                this.doFeedback('Datos guardados correctamente', 'success')
                this.header.breadcrumb[1].name = this.form.NombreCIF
                this.$store.commit('app/setheaderBreadCrumb', this.form.NombreCIF)
                this.resetControlFormChanges()
              } else {
                this.doFeedback(response.msgError + ' [Código de error: ' + response.codError + ']')
              }
            }
            this.$store.commit('setIsLoading', false)
          } else {
            this.doFeedback('Error al procesar los datos, revise la información')
            this.$store.commit('setIsLoading', false)
            return false
          }
        })
      }, 100)
    },
    resetForm () {
      this.$refs.ruleForm.resetFields()
    },
    goBack: function () {
      this.$router.push({ name: 'companies' })
    },
    goDetailCert: function () {
      const idEmpresa = this.form.clienteId
      const serial = this.form.SerialNumber
      const tipo = this.form.TipoCertificado
      this.$router.push({ name: 'certificates/detail', params: { idEmpresa, serial, tipo } })
    },
    goDetailClient () {
      const id = this.form.clienteId
      this.$router.push({ name: 'configurations/clients/details', params: { id } })
    },
    downloadCompanyData: async function (companyId) {
      try {
        this.$store.commit('setIsLoading', true)

        const params = { IdCifEmpresa: companyId }
        const company = await getCompany(params)
        if (company.blnError === true) {
          this.doFeedback(company.msgError + ' [Código de error: ' + company.codError + ']')
        } else {
          this.setCompany(company.CIFSEmpresa[0], company.TipoServicio)
        }
      } catch (error) {
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    setCompany: function (company, tipoServicio = null) {
      if (company) {
        this.form.NombreCIF = company.NombreCIF ? company.NombreCIF : null
        this.form.CIF = company.CIF ? company.CIF : null
        this.form.EmailAvisoNueva = company.EmailAvisoNueva ? company.EmailAvisoNueva : null
        this.form.EmailAvisoDescargada = company.EmailAvisoDescargada ? company.EmailAvisoDescargada : null
        this.form.SMSAvisoDescargada = company.NumeroSMSDescargadas ? company.NumeroSMSDescargadas.substring(4) : null
        this.form.EmailAvisoTramitada = company.EmailAvisoTramitada ? company.EmailAvisoTramitada : null
        this.form.EmailAvisoUltimaLlamada = company.EmailDiasAviso ? company.EmailDiasAviso : null
        this.form.EmailAvisoTramitacionUrgente = company.EmailAvisoTramitacionUrgente ? company.EmailAvisoTramitacionUrgente : null
        this.form.EmailAvisoErrores = company.EmailAvisoErrores ? company.EmailAvisoErrores : null
        this.form.AgruparEmailsAvisos = company.AgruparEmailsAvisos ? company.AgruparEmailsAvisos : false
        this.form.EmailTecnico = company.EmailTecnico ? company.EmailTecnico : null
        this.form.FechaAlta = company.FechaAlta ? company.FechaAlta : null
        this.form.FechaBaja = company.FechaBaja ? moment(company.FechaBaja).format('DD-MM-YYYY HH:mm') : null
        this.form.CodigoExt = company.CodigoExt ? company.CodigoExt : null
        this.form.DescargaAutomatica = company.DescargaAutomatica
        this.form.EnviarAvisos = company.EnviarAvisos
        this.form.DiasDescargaAutomatica = company.DiasDescargaAutomatica ? company.DiasDescargaAutomatica : 0
        this.form.Activo = company.Activo
        this.form.ConPoderesDelegados = company.ConPoderesDelegados
        this.form.DiasAviso = company.DiasAviso !== null || company.DiasAviso !== undefined ? company.DiasAviso : null
        this.form.DiasAvisoFinTramite = company.DiasFinTramite !== null || company.DiasFinTramite !== undefined ? company.DiasFinTramite : null
        this.form.clienteId = company.IdEmpresa ? company.IdEmpresa : null
        this.form.CN = company.CN ? company.CN : null
        this.form.verDetalle = company.verDetalle ? company.verDetalle: null
        this.form.SerialNumber = company.SerialNumber ? company.SerialNumber : null
        this.form.TipoCertificado = company.TipoCertificado ? company.TipoCertificado : null
        this.form.fechaInicioVig = company.FechaInicioVigilancia ? company.FechaInicioVigilancia : null
        this.form.idTipoServicio = tipoServicio?.IdTipoServicio ? tipoServicio?.IdTipoServicio : null
      }
      this.resetControlFormChanges()
    },
    resetCompany: function () {
      this.form.NombreCIF = null
      this.form.CIF = null
      this.form.EmailTecnico = null
      this.form.FechaAlta = null
      this.form.FechaBaja = null
      this.form.CodigoExt = null
      this.form.DescargaAutomatica = false
      this.form.EnviarAvisos = true
      this.form.DiasDescargaAutomatica = 0
      this.form.Activo = true
      this.form.ConPoderesDelegados = false
      this.form.clienteId = null
      this.form.CN = null
      this.form.verDetalle = false
      this.form.SerialNumber = null
      this.form.TipoCertificado = null
      this.form.fechaInicioVig = null
      this.form.idTipoServicio = null
      this.resetControlFormChanges()
    }
  }

}
</script>

<style scoped>
.box {
  background-color: #444;
  color: #fff;
  border-radius: 5px;
  border: 1px solid black;
  padding: 5px 20px;
}
.grid {
  display: grid;
  grid-gap: 10px;
}
.wrapper {
  grid-template-columns: 1fr 1fr ;
  background-color: seagreen;
  padding: 10px;
  width: 50%;
  margin: 20px auto;
}
.grid.left {
  grid-column: 1 / span 1;
  grid-auto-rows: min-content;
}
.grid.right {
  grid-column: span 1 / -1;
  grid-auto-rows: min-content;
}
.tip {
  grid-column: 1 / -1;
}
.pic>img {
  max-width: 100%;
  max-height: 100%;
}

</style>

<style lang="sass" scoped>
</style>
